import moment from 'moment'
import 'moment-timezone'
import * as yup from 'yup'

type GmtOffset = '+7' | '+8' | '+9'

export function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes'

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`
}

export function shortenNumber(num: number | string | undefined, config = { decimalSeparator: '.', fixed: 0 }) {
  if (!num || num.toString().trim() === '') {
    return '0'
  }
  const { decimalSeparator = ',', fixed } = config
  const parsedNum = typeof num === 'string' ? Number(num) : num

  const suffixes = ['', 'k', 'm', 'b', 't']
  let suffixIndex = 0
  let formattedNum = parsedNum
  while (formattedNum >= 1000 && suffixIndex < suffixes.length - 1) {
    formattedNum /= 1000
    suffixIndex++
  }
  return formattedNum.toFixed(fixed).replace('.', decimalSeparator) + suffixes[suffixIndex]
}

export function truncateFilename(filename: string, maxLength: number = 40): string {
  if (filename.length <= maxLength) {
    return filename
  } else {
    const extensionIndex = filename.lastIndexOf('.')
    const extension = filename.substring(extensionIndex + 1)
    const truncatedName = filename.substring(0, maxLength - 7 - extension.length) // 7 accounts for '..._.'
    return truncatedName + '..._.' + extension
  }
}

export function urlToFilename(url: string): string {
  const path = new URL(url).pathname
  const filename = path.substring(path.lastIndexOf('/') + 1)
  const decodedFilename = decodeURIComponent(filename)
  return decodedFilename
}

export function formatDistance(meters: number): string {
  if (meters >= 1000) {
    const kilometers = meters / 1000
    const formattedKm = kilometers.toFixed(1).replace(/\.0$/, '') // Remove '.0'
    return `${formattedKm} km`
  } else {
    return `${meters} m`
  }
}

// Helper function to format NPWP/NIK
export const formatNPWPNIK = (value: string) => {
  // Remove any non-digit characters
  const cleanValue = value.replace(/\D/g, '')

  // If it's 15 digits (NPWP), format it as XX.XXX.XXX.X-XXX.XXX
  if (cleanValue.length === 15) {
    return cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{1})(\d{3})(\d{3})/, '$1.$2.$3.$4-$5.$6')
  }

  // If it's 16 digits (NIK), return as is
  return cleanValue.slice(0, 16)
}

export const toReadableDate = (value?: string) => {
  return value ? moment(value).format('DD MMMM YYYY') : '-'
}

export const removeHtmlTags = (char: string): string => {
  return char.replace(/<[^>]*>/g, '')
}

export class SpecialistCodeValidator {
  private static readonly ERROR_MESSAGES = {
    required: 'Specialist code is required',
    spaces: 'Spaces are not allowed in specialist code',
    uppercase: 'Specialist code must be in uppercase',
    specialChars: 'Only letters and numbers are allowed',
    minLength: 'Specialist code must be at least ${min} characters',
    maxLength: 'Specialist code must not exceed ${max} characters',
  }

  private static readonly VALIDATION_TESTS = {
    noSpaces: {
      name: 'no-spaces',
      test: (value: string) => !value?.includes(' '),
      message: SpecialistCodeValidator.ERROR_MESSAGES.spaces,
    },
    uppercase: {
      name: 'uppercase-only',
      test: (value: string) => value === value?.toUpperCase(),
      message: SpecialistCodeValidator.ERROR_MESSAGES.uppercase,
    },
    alphanumeric: {
      name: 'alphanumeric-only',
      test: (value: string) => /^[A-Z0-9]+$/.test(value),
      message: SpecialistCodeValidator.ERROR_MESSAGES.specialChars,
    },
  }

  public static createValidation(
    options: {
      required?: boolean
      minLength?: number
      maxLength?: number
      customMessages?: Partial<typeof SpecialistCodeValidator.ERROR_MESSAGES>
    } = {},
  ) {
    const { required = true, minLength, maxLength, customMessages = {} } = options

    const messages = { ...this.ERROR_MESSAGES, ...customMessages }
    let schema = yup.string().trim()

    // Apply required validation if specified
    if (required) {
      schema = schema.required(messages.required)
    }

    // Apply length validations if specified
    if (minLength !== undefined) {
      schema = schema.min(minLength, messages.minLength)
    }
    if (maxLength !== undefined) {
      schema = schema.max(maxLength, messages.maxLength)
    }

    // Apply all validation tests
    Object.values(this.VALIDATION_TESTS).forEach((test: any) => {
      schema = schema.test(test)
    })

    return schema
  }
}

export const formatTime = (dateTime: string): string => {
  const time = moment(dateTime)
  const timezone = moment.tz.guess()
  const offset = moment.tz(timezone).utcOffset() / 60
  let gmtOffset: GmtOffset = '+7' // Default WIB
  if (offset === 8) gmtOffset = '+8' // WITA
  if (offset === 9) gmtOffset = '+9' // WIT
  return time.format(`HH:mm [GMT]${gmtOffset}`)
}
